/**
 * chatMessageMapper — the SINGLE source of truth for turning a persisted
 * message from `GET /chat/conversations/{id}/messages` into the front-end
 * message shape.
 *
 * Why this exists: there are two history loaders — `useChatStream`
 * (/assistant fullpage + admin) and `ionConversationService`
 * (slide-over). They MUST produce the identical shape, because
 * MarkdownRenderer / chatStore resolve the same markers from it. They used
 * to map inline, independently — and drifted: the slide-over loader once
 * hardcoded `toolCalls: []`, so every value chip broke on reopen-from-
 * history while the fullpage worked. One mapper makes that class of drift
 * impossible.
 *
 * Returns a SUPERSET shape (sources / kpiGroups / figures / stats) so both
 * stores can consume it; extra fields are harmless to either.
 */

/**
 * One persisted tool call → the live in-memory shape (matches
 * chatStore.startToolCall + endToolCall). Accepts camelCase (FastAPI
 * alias) OR snake_case (raw dict passthrough / older clients). The
 * `output` envelope ({result:{artifact_type…}}) is what chips/plots
 * resolve against — never drop it.
 */
export function mapApiToolCall(c) {
  return {
    id: c.id,
    name: c.name,
    input: c.input || {},
    output: c.output || null,
    status: c.status || 'done',
    isError: Boolean(c.isError ?? c.is_error),
    startedAt: c.startedAt ?? c.started_at_ms ?? null,
    endedAt: c.endedAt ?? c.ended_at_ms ?? null,
  }
}

/** One persisted message → the canonical FE message shape. */
export function mapApiMessage(m) {
  return {
    id: String(m.id),
    role: m.role,
    content: m.content || '',
    status: 'complete',
    createdAt: m.createdAt || m.created_at || new Date().toISOString(),
    sources: {},
    kpiGroups: [],
    figures: [],
    stats: {},
    toolCalls: (m.toolCalls || m.tool_calls || []).map(mapApiToolCall),
    // S4: the onboarding tour's auto-sent canonical prompt. Same `meta` shape
    // the optimistic local send produces (useChatStream), so a reloaded
    // conversation renders the chip identically.
    ...(m.example ? { meta: { example: true } } : {}),
  }
}
